import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { Color } from "./color";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";

interface colorslice {
  colors: Color[];
  formData: Partial<Color>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: colorslice = {
  colors: [],
  formData: { color_name: "", status: 1 },
  loading: false,
  error: null,
  pagination: null,
};

//get list
export const fetchColors = createAsyncThunk(
  "color/fetchColors",
  async (params?: { page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);

      const response = await enhancedApiClient.get("/master/color?" + queryParams.toString());
      if (isSuccessResponse(response)) {
        console.log("Fetch color response:", response.data);
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch colors");
      }
    } catch (error: any) {
      console.error("Fetch colors error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch colors";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Create State
export const createColor = createAsyncThunk(
  "state/createColor",
  async (data: Partial<Color>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/master/color", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create color");
      }
    } catch (error: any) {
      console.error("Create color error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create color";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Edit State
export const editColor = createAsyncThunk(
  "color/editColor",
  async ({ id, data }: { id: string; data: Partial<Color> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/master/color/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update color");
      }
    } catch (error: any) {
      console.error("Edit color error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update color";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Toggle color status
export const toggleColorStatus = createAsyncThunk(
  "color/toggleColorStatus",
  async ({ id, status }: { id: number; status: number }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.patch(`/master/color/${id}/status`, { status });

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to toggle color status");
      }
    } catch (error: any) {
      console.error("Toggle color status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to toggle color status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const colorslice = createSlice({
  name: "color",
  initialState,
  reducers: {
    setStateName: (state, action: PayloadAction<string>) => {
      state.formData.color_name = action.payload;
    },
    setcolorstatus: (state, action: PayloadAction<string>) => {
      state.formData.status = action.payload;
    },
    resetForm: (state) => {
      state.formData = { color_name: "", status: "active" };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchColors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchColors.fulfilled, (state, action) => {
        state.loading = false;
        state.colors = action.payload.records.map((record: any) => ({
          id: record.colorId,
          colorId:record.colorId,
          color_name: record.colorName,
          user_name: record.userName,
          status: record.status,
          updated_at: record.updatedOn,
          updated_by: record.updatedBy,
          action: record.action,
        }));
        state.pagination = action.payload.pagination;
        console.log("colors fetched successfully:", action.payload);
      })
      .addCase(fetchColors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createColor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createColor.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if(action.payload) {
          const newColor: Color = {
            id: action.payload.colorId,
            colorId:action.payload.colorId,
            color_name: action.payload.colorName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action: action.payload.action,
          };
          state.colors.unshift(newColor); 
          }
        state.formData = { color_name: "", status: "active" };
      })
      .addCase(createColor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit
      .addCase(editColor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editColor.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedColor : Color = {
          id: action.payload.colorId,
          colorId:action.payload.colorId,
          color_name: action.payload.colorName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action: action.payload.action,
        }
        const index = state.colors.findIndex(s => s.id === updatedColor.id);
        if (index !== -1) {
          state.colors[index] = updatedColor;
        }
        state.formData = { color_name: "", status: "active" };
      })
      .addCase(editColor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Toggle Status
      .addCase(toggleColorStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleColorStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedColor : Color = {
          id: action.payload.colorId,
          colorId:action.payload.colorId,
          color_name: action.payload.colorName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action: action.payload.action,
        }
        const index = state.colors.findIndex(s => s.id === updatedColor.id);
        if (index !== -1) {
          state.colors[index] = updatedColor;
        }
      })
      .addCase(toggleColorStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

  },
});

export const { setStateName, setcolorstatus, resetForm } = colorslice.actions;
// ✅ Selectors
export const selectedColors = (state: RootState) => state.color.colors;
export const selectColorLoading = (state: RootState) => state.color.loading;
export const selectColorError = (state: RootState) => state.color.error;
export const selectColorPagination = (state: RootState) => state.color.pagination

export default colorslice.reducer;
